// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ToastProvider } from './components/ToastNotification';
import { WebsiteBuilderProvider } from './contexts/WebsiteBuilderContext';
import SubdomainRouter from './components/SubdomainRouter';

// Styles
import './styles/divine-frameworks-colors.css';
import './App.css';
import { trackPageView } from './utils/analytics';

// localStorage debugging utilities (makes window.clearSiteData() etc available in console)
import './utils/localStorageCleanup';

// One-time admin fix utility (makes window.fixAdminAccount() available in console)
import './utils/fixAdminAccount';

// Authentication Pages
import Login from './pages/Login';
import Signup from './pages/Signup';

// Layout & Protection
import Layout from './components/Layout';
import ProtectedRoute from './routes/ProtectedRoute';

// Main Pages
import Pricing from './pages/Pricing';
import About from './pages/About';
import Contact from './pages/Contact';
import Home from './pages/Home';

// Checkout & Booking
import Checkout from './pages/Checkout';
import CheckoutSuccess from './pages/CheckoutSuccess';
import BookingConfirmation from './pages/BookingConfirmation';
import BookingSuccessPage from './pages/BookingSuccessPage';
import BookingManagement from './pages/BookingManagement';
import ProposalResponse from './pages/ProposalResponse';
import ClientSessionHub from './pages/ClientSessionHub';
import RoleSelection from './pages/RoleSelection';
import Learn from './pages/Learn';
import LearnTopic from './pages/LearnTopic';

// Practitioner Pages
import Directory from './pages/Directory';

// Dashboard
import PractitionerDashboard from './pages/dashboard/PractitionerDashboard';
import CalendarView from './pages/dashboard/CalendarView';
import NotificationSettings from './pages/dashboard/NotificationSettings';

// Onboarding
import Onboarding from './pages/Onboarding';

// Practitioner Tools
import EventsTab from './components/events/EventsTab';
import Analytics from './pages/dashboard/Analytics';
import ClientManagement from './pages/ClientManagement';
import ClientProfile from './pages/dashboard/ClientProfile';
import SessionPrep from './pages/dashboard/SessionPrep';
import IntakeFormBuilder from './pages/dashboard/IntakeFormBuilder';
import EventDashboard from './pages/EventDashboard';
import IntakeFormPage from './pages/public/IntakeFormPage';
import Reports from './pages/dashboard/Reports';
import Earnings from './pages/dashboard/Earnings';

// Account Management
import Settings from './pages/dashboard/Settings';
import AccountSettings from './pages/dashboard/AccountSettings';
import PublicProfile from './pages/dashboard/PublicProfile';
import AvailabilitySettings from './pages/dashboard/AvailabilitySettings';
import BusinessPaymentsSettings from './pages/dashboard/BusinessPaymentsSettings';
import PoliciesSettings from './pages/dashboard/PoliciesSettings';
import Support from './pages/dashboard/Support';
import HelpCenterTopic from './pages/dashboard/HelpCenterTopic';
import NotificationPreferences from './pages/NotificationPreferences';
import SubscriptionManagement from './pages/SubscriptionManagement';
import ConnectWebsite from './pages/dashboard/ConnectWebsite';
import WidgetConfig from './pages/dashboard/WidgetConfig';
import AdminReviewQueue from './pages/admin/AdminReviewQueue';
import NotFound from './pages/NotFound';
import VerificationDashboard from './pages/dashboard/VerificationDashboard';
import IntegrationsSettings from './pages/dashboard/IntegrationsSettings';

// Website Preview
import WebsitePreview from './pages/preview/WebsitePreview';

// Public Website (practitioner's public site at /site/:slug)
import PublicWebsite from './pages/PublicWebsite';
import EventDetailPage from './pages/EventDetailPage';

// Site Editor
import SiteEditor from './pages/SiteEditor';

// Business Playbook
import BusinessPlaybook from './pages/BusinessPlaybook';
import PlaybookTopic from './pages/PlaybookTopic';

// Messaging
import Messages from './pages/Messages';
import MessageThread from './pages/MessageThread';
import ComposeMessage from './pages/ComposeMessage';

// Reviews
import Reviews from './pages/dashboard/Reviews';
import ReviewPage from './pages/public/ReviewPage';

// Marketing
import Marketing from './pages/dashboard/Marketing';
import NewsletterCompose from './pages/dashboard/NewsletterCompose';

// Coming Soon

// Development/Testing (remove before production)
import FirebaseTest from './pages/FirebaseTest';

// Coming Soon
import ComingSoon from './pages/ComingSoon';

// Scrolls to top on every route change + tracks page view for GA4
const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname + search);
  }, [pathname, search]);
  return null;
};

// ---------------------------------------------------------------------------
// HomeRoute
// Unauthenticated visitors at "/" see the Coming Soon page.
// Authenticated practitioners are redirected to /dashboard.
// Uses the same safe auth pattern (done flag + 3s timeout) to avoid
// showing a flash on authenticated page loads.
// ---------------------------------------------------------------------------
const HomeRoute = () => {
  const [authChecked, setAuthChecked] = React.useState(false);
  const [isAuthed, setIsAuthed] = React.useState(false);

  React.useEffect(() => {
    const { auth } = require('./config/firebase-config');
    const { onAuthStateChanged } = require('firebase/auth');
    let done = false;
    const timeout = setTimeout(() => {
      if (!done) { done = true; setAuthChecked(true); }
    }, 3000);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!done) {
        done = true;
        clearTimeout(timeout);
        setIsAuthed(!!user);
        setAuthChecked(true);
      }
    });
    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  if (!authChecked) return null; // brief — avoids flash
  if (isAuthed) {
    const { Navigate } = require('react-router-dom');
    const cachedRole = localStorage.getItem('userRole');
    if (cachedRole === 'seeker') return <Navigate to="/my-sessions" replace />;
    if (cachedRole === 'pending') return <Navigate to="/welcome" replace />;
    return <Navigate to="/dashboard" replace />; // practitioner/admin/default
  }
  return <ComingSoon />;
};

const HomePreviewRoute = () => {
  const [status, setStatus] = React.useState('checking');

  React.useEffect(() => {
    const { auth } = require('./config/firebase-config');
    const { onAuthStateChanged } = require('firebase/auth');
    let done = false;
    const timeout = setTimeout(() => {
      if (!done) { done = true; setStatus('denied'); }
    }, 3000);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!done) {
        done = true;
        clearTimeout(timeout);
        if (user && user.email?.toLowerCase() === 'pac623@icloud.com') {
          setStatus('allowed');
        } else {
          setStatus('denied');
        }
      }
    });
    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  if (status === 'checking') return null;
  if (status === 'denied') {
    const { Navigate } = require('react-router-dom');
    return <Navigate to="/" replace />;
  }
  return <Home />;
};

function App() {
  return (
    <HelmetProvider>
    <Router>
      <ScrollToTop />
      <ToastProvider>
        <SubdomainRouter>
          <Routes>
          {/* Homepage — no Layout, no nav. Coming Soon for visitors, /dashboard redirect for authenticated */}
          <Route index element={<HomeRoute />} />

          {/* Admin-only homepage preview (while Coming Soon is active for public) */}
          <Route path="/home-preview" element={<HomePreviewRoute />} />

          {/* ── Public routes — no WebsiteBuilderProvider (no Firebase writes on public loads) ── */}
          <Route path="/login" element={<Login />} />
          <Route path="/admin-access" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/firebase-test" element={<FirebaseTest />} />
          <Route path="/booking/success" element={<BookingSuccessPage />} />
          <Route path="/booking/manage/:bookingId/:token" element={<BookingManagement />} />
          <Route path="/booking/proposal/:bookingId/:token" element={<ProposalResponse />} />

          {/* Public Contact page — standalone, no Layout, no auth */}
          <Route path="/contact" element={<Contact />} />

          {/* Public Website — NO WebsiteBuilderProvider. Visitors must never trigger
              the context's auth listener or background sync. */}
          <Route path="/site/:slug" element={<PublicWebsite />} />
          <Route path="/site/:slug/events/:eventSlug" element={<EventDetailPage />} />

          {/* Intake Form — public, no auth. Client fills out via emailed link. */}
          <Route path="/intake/:practitionerId/:token" element={<IntakeFormPage />} />

          {/* Review Page — public, no auth. Client submits review via emailed link. */}
          <Route path="/review/:reviewId/:token" element={<ReviewPage />} />

          {/* ── Authenticated / editor routes — wrapped in WebsiteBuilderProvider ── */}
          <Route element={<WebsiteBuilderProvider><Outlet /></WebsiteBuilderProvider>}>

          {/* Onboarding (No Layout - distraction-free) */}
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          } />

          {/* Role Selection (No Layout - one-time after signup) */}
          <Route path="/welcome" element={
            <ProtectedRoute>
              <RoleSelection />
            </ProtectedRoute>
          } />

          {/* Site Editor (No Layout - full-screen editor) */}
          <Route path="/site-editor" element={
            <ProtectedRoute>
              <SiteEditor />
            </ProtectedRoute>
          } />

          {/* Main Application (With Layout) */}
          <Route path="/" element={<Layout />}>

            <Route path="practitioners" element={<Navigate to="/directory" replace />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="about" element={<About />} />
            {/* Help Center — role-aware, accessible to both seekers and practitioners */}
            <Route path="help" element={
              <ProtectedRoute>
                <Support />
              </ProtectedRoute>
            } />
            <Route path="help/:categorySlug" element={
              <ProtectedRoute>
                <HelpCenterTopic />
              </ProtectedRoute>
            } />

            {/* Seeker pages (same Layout nav, role-aware) */}
            <Route path="my-sessions" element={
              <ProtectedRoute>
                <ClientSessionHub />
              </ProtectedRoute>
            } />
            <Route path="learn" element={
              <ProtectedRoute>
                <Learn />
              </ProtectedRoute>
            } />
            <Route path="learn/:topicSlug" element={
              <ProtectedRoute>
                <LearnTopic />
              </ProtectedRoute>
            } />
            <Route path="learn/:topicSlug/:articleSlug" element={
              <ProtectedRoute>
                <LearnTopic />
              </ProtectedRoute>
            } />

            {/* Practitioner Discovery (Public) */}
            <Route path="directory" element={<Directory />} />

            {/* Checkout & Booking */}
            <Route path="checkout" element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            } />
            <Route path="checkout/success" element={
              <ProtectedRoute>
                <CheckoutSuccess />
              </ProtectedRoute>
            } />
            <Route path="booking-confirmation" element={
              <ProtectedRoute>
                <BookingConfirmation />
              </ProtectedRoute>
            } />

            {/* Dashboard */}
            <Route path="dashboard" element={
              <ProtectedRoute>
                <PractitionerDashboard />
              </ProtectedRoute>
            } />
            <Route path="dashboard/practitioner" element={
              <ProtectedRoute>
                <PractitionerDashboard />
              </ProtectedRoute>
            } />
            <Route path="dashboard/calendar" element={
              <ProtectedRoute>
                <CalendarView />
              </ProtectedRoute>
            } />

            {/* Practitioner Business Tools */}
            <Route path="dashboard/services" element={
              <ProtectedRoute>
                <EventsTab />
              </ProtectedRoute>
            } />
            <Route path="dashboard/events/:eventId" element={
              <ProtectedRoute>
                <EventDashboard />
              </ProtectedRoute>
            } />
            <Route path="dashboard/analytics" element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            } />
            <Route path="dashboard/clients" element={
              <ProtectedRoute>
                <ClientManagement />
              </ProtectedRoute>
            } />
            <Route path="dashboard/clients/:clientId" element={
              <ProtectedRoute>
                <ClientProfile />
              </ProtectedRoute>
            } />
            <Route path="dashboard/prep/:clientId" element={
              <ProtectedRoute>
                <SessionPrep />
              </ProtectedRoute>
            } />
            <Route path="dashboard/intake-forms" element={
              <ProtectedRoute>
                <IntakeFormBuilder />
              </ProtectedRoute>
            } />
            <Route path="dashboard/intake-forms/:formId" element={
              <ProtectedRoute>
                <IntakeFormBuilder />
              </ProtectedRoute>
            } />
            <Route path="dashboard/reports" element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            } />
            <Route path="dashboard/earnings" element={
              <ProtectedRoute>
                <Earnings />
              </ProtectedRoute>
            } />

            {/* Account Management */}
            <Route path="dashboard/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/account" element={
              <ProtectedRoute>
                <AccountSettings />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/profile" element={
              <ProtectedRoute>
                <PublicProfile />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/availability" element={
              <ProtectedRoute>
                <AvailabilitySettings />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/business" element={
              <ProtectedRoute>
                <BusinessPaymentsSettings />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/policies" element={
              <ProtectedRoute>
                <PoliciesSettings />
              </ProtectedRoute>
            } />
            <Route path="preview" element={
              <ProtectedRoute>
                <WebsitePreview />
              </ProtectedRoute>
            } />
            <Route path="preview/website" element={
              <ProtectedRoute>
                <WebsitePreview />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/notifications" element={
              <ProtectedRoute>
                <NotificationSettings />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/support" element={
              <ProtectedRoute>
                <Support />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/support/:categorySlug" element={
              <ProtectedRoute>
                <HelpCenterTopic />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/connect-website" element={
              <ProtectedRoute>
                <ConnectWebsite />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/integrations" element={
              <ProtectedRoute>
                <IntegrationsSettings />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/widget" element={
              <ProtectedRoute>
                <WidgetConfig />
              </ProtectedRoute>
            } />
            <Route path="dashboard/settings/verification" element={
              <ProtectedRoute>
                <VerificationDashboard />
              </ProtectedRoute>
            } />
            <Route path="dashboard/notifications" element={
              <ProtectedRoute>
                <NotificationPreferences />
              </ProtectedRoute>
            } />
            <Route path="dashboard/subscription" element={
              <ProtectedRoute>
                <SubscriptionManagement />
              </ProtectedRoute>
            } />

            {/* Business Playbook */}
            <Route path="business-playbook" element={
              <ProtectedRoute>
                <BusinessPlaybook />
              </ProtectedRoute>
            } />
            <Route path="business-playbook/:topicSlug" element={
              <ProtectedRoute>
                <PlaybookTopic />
              </ProtectedRoute>
            } />
            <Route path="business-playbook/:topicSlug/:articleSlug" element={
              <ProtectedRoute>
                <PlaybookTopic />
              </ProtectedRoute>
            } />

            {/* Messaging */}
            <Route path="messages" element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            } />
            <Route path="messages/compose" element={
              <ProtectedRoute>
                <ComposeMessage />
              </ProtectedRoute>
            } />
            <Route path="messages/:messageId" element={
              <ProtectedRoute>
                <MessageThread />
              </ProtectedRoute>
            } />

            {/* Reviews */}
            <Route path="dashboard/reviews" element={
              <ProtectedRoute>
                <Reviews />
              </ProtectedRoute>
            } />

            {/* Marketing */}
            <Route path="dashboard/marketing" element={
              <ProtectedRoute>
                <Marketing />
              </ProtectedRoute>
            } />
            <Route path="dashboard/marketing/compose" element={
              <ProtectedRoute>
                <NewsletterCompose />
              </ProtectedRoute>
            } />

            {/* Admin — hidden, admin-only */}
            <Route path="admin/reviews" element={
              <ProtectedRoute>
                <AdminReviewQueue />
              </ProtectedRoute>
            } />

            {/* Catch-all 404 — must remain last child of Layout */}
            <Route path="*" element={<NotFound />} />
          </Route>

          </Route> {/* end WebsiteBuilderProvider layout route */}
          </Routes>
        </SubdomainRouter>
      </ToastProvider>
    </Router>
    </HelmetProvider>
  );
}

export default App;